import { useEffect, useRef, useState } from "react";

/**
 * Sticky only while sticky can actually do its job.
 *
 * `position: sticky` is wrong in two situations, and a media query can only
 * approximate either one:
 *
 *   - the element has been stacked above its neighbour rather than set beside
 *     it, where pinning it parks it on top of the content being scrolled;
 *   - the viewport is too short to show the whole element, where pinning it
 *     hides its own lower half for as long as the reader keeps scrolling.
 *
 * Both are measured rather than guessed, so the element's content can grow —
 * another line of copy, another button — without anyone having to remember to
 * retune a breakpoint that no longer matches its height.
 *
 * Returns `[ref, isSticky]`. Attach the ref to the element and apply your
 * sticky class when `isSticky`.
 */

/**
 * Breathing room under the pinned element. Without it, "fits" is true at the
 * exact pixel where the element's last border meets the bottom of the screen,
 * which reads as broken rather than as fitting.
 */
const BOTTOM_GUTTER = 24;

export function useStickyWhenItFits() {
	const ref = useRef(null);
	// Starts false so the prerendered markup and the first client paint agree,
	// and so the honest default is the one that cannot misbehave.
	const [isSticky, setIsSticky] = useState(false);

	useEffect(() => {
		const el = ref.current;
		if (!el) return;

		const measure = () => {
			const rect = el.getBoundingClientRect();
			if (rect.width === 0) return;

			/*
			 * Read off the layout rather than off a breakpoint. Two boxes are
			 * side by side exactly when their horizontal ranges do not overlap;
			 * once the grid stacks them they share a column and this is false.
			 * That keeps the 900px breakpoint in Landing.module.css as the one
			 * place it is written down, and it survives the parent switching
			 * between grid and flex.
			 */
			const sideBySide = [...el.parentElement.children]
				.filter((child) => child !== el)
				.some((child) => {
					const other = child.getBoundingClientRect();
					if (other.width === 0) return false;
					return (
						other.left >= rect.right - 1 ||
						other.right <= rect.left + 1
					);
				});

			/*
			 * The sticky offset lives in the stylesheet. `top` resolves to a
			 * pixel length through getComputedStyle even while the element is
			 * static, so it can be read here without the CSS value having to be
			 * repeated as a constant. `auto` would give NaN, hence the guard.
			 */
			const offset = parseFloat(getComputedStyle(el).top);
			const fits =
				rect.height + (Number.isNaN(offset) ? 0 : offset) +
					BOTTOM_GUTTER <=
				window.innerHeight;

			setIsSticky(sideBySide && fits);
		};

		measure();

		/*
		 * Nothing measured above changes when the class is toggled — the
		 * element keeps its size and its horizontal position whether or not it
		 * is pinned — so this cannot drive itself in a loop.
		 */
		const observer = new ResizeObserver(measure);
		observer.observe(el);
		if (el.parentElement) observer.observe(el.parentElement);

		// A viewport that only gets shorter resizes nothing inside the page,
		// so the element's own observer never fires for it.
		window.addEventListener("resize", measure);

		return () => {
			observer.disconnect();
			window.removeEventListener("resize", measure);
		};
	}, []);

	return [ref, isSticky];
}
